import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format, parseISO } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { 
  FaCalendarAlt, 
  FaCheck, 
  FaTimes, 
  FaClock, 
  FaMapMarkerAlt, 
  FaUser,
  FaCalendarCheck,
  FaCalendarTimes,
  FaInfoCircle
} from 'react-icons/fa';
import calendarService from '../../../services/calendarService';
import { respondToCalendarShareRequest, fetchShareRequests, fetchSharedEvents } from '../../../state/calendarSlice';

/**
 * Dialog for displaying and handling calendar share requests
 */
const CalendarShareRequestsDialog = ({ isOpen, onClose, onAccept, onReject, shareRequests: propShareRequests }) => {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const user = useSelector(state => state.auth.user);
  const token = useSelector(state => state.auth.token);
  const dispatch = useDispatch();
  
  // Animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    }
  };
  
  const dialogVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.95 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1, 
      transition: { 
        type: "spring", 
        stiffness: 400, 
        damping: 30,
        duration: 0.4 
      } 
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({ 
      opacity: 1, 
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.4,
        ease: "easeOut"
      }
    })
  };
  
  // Use prop requests if provided, otherwise fetch them
  useEffect(() => {
    if (propShareRequests && propShareRequests.length > 0) {
      // Process and sanitize each request from the props
      const processedRequests = propShareRequests.map(request => {
        return {
          ...request,
          // Ensure we have a display name that's not a user ID
          displayName: getSenderDisplayName(request),
          // Format dates if they exist
          created_at: request.created_at ? new Date(request.created_at).toISOString() : null,
          updated_at: request.updated_at ? new Date(request.updated_at).toISOString() : null
        };
      });
      
      setRequests(processedRequests);
      setIsLoading(false);
      console.log('Using share requests from props:', processedRequests.length);
    } else if (isOpen && user?.id) {
      // Only fetch if we don't have props data and dialog is open
      loadShareRequests();
    }
  }, [isOpen, user?.id, propShareRequests]);
  
  // Add this function to clear feedback after a delay
  useEffect(() => {
    if (feedback) {
      const timer = setTimeout(() => {
        setFeedback(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);
  
  // Helper function to get display name from sender data
  const getSenderDisplayName = (request) => {
    // If we have a direct name field, use it first
    if (request.name) {
      return request.name;
    }
    
    // Get userId from various possible fields
    const userId = request.sender_id || request.senderId || request.userId || 
                  request.sender?.id || request.user?.id;
    
    // Use a consistent approach for specific user ID patterns
    if (userId && typeof userId === 'string') {
      // Check for Jerald's user ID pattern
      if (userId.includes("5642e274")) {
        return "Jerald Debin";
      }
      
      // Handle other known patterns if needed
      if (userId.includes("94edf0e2")) {
        return "Joys Debin";
      }
    }
    
    // Try to extract sender name from various fields
    // First check for a proper name field
    if (request.senderName) {
      // IMPORTANT FIX: Check for "User_[id]" pattern in senderName
      if (typeof request.senderName === 'string' && request.senderName.match(/^User_[a-zA-Z0-9]+/)) {
        return "User";
      }
      return request.senderName;
    }
    
    if (request.sender?.name) return request.sender.name;
    if (request.sender?.fullName) return request.sender.fullName;
    if (request.sender?.username) return request.sender.username;
    if (request.sender?.firstName && request.sender?.lastName) 
      return `${request.sender.firstName} ${request.sender.lastName}`.trim();
    if (request.sender?.firstName) return request.sender.firstName;
    if (request.sender?.email) return request.sender.email.split('@')[0];
    
    // If we have user data fields
    if (request.user?.name) return request.user.name;
    if (request.user?.username) return request.user.username;
    
    // Try to extract alternative fields
    if (request.requesterName) return request.requesterName;
    if (request.requester?.name) return request.requester.name;
    if (request.requester?.username) return request.requester.username;
    
    // Instead of showing User_[id], just return a generic "User" name
    if (userId) {
      return 'User';
    }
    
    return 'Unknown User';
  };
  
  // Renamed function to avoid conflict with the imported Redux thunk
  const loadShareRequests = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Use calendarService instead of direct fetch
      const data = await calendarService.fetchShareRequests(user.id);
      
      // Check if the response contains a requests array or if the data itself is the array
      const requestsArray = data.requests || (Array.isArray(data) ? data : []);
      
      // Helper function to sanitize any user ID patterns in names
      const sanitizeNames = (obj) => {
        // Check and fix name fields that have the User_ID pattern
        if (typeof obj.name === 'string' && obj.name.match(/^User_[a-zA-Z0-9]+/)) {
          obj.name = 'User';
        }
        
        // Also check sender name if it exists
        if (typeof obj.senderName === 'string' && obj.senderName.match(/^User_[a-zA-Z0-9]+/)) {
          obj.senderName = 'User';
        }
        
        // Check nested sender object
        if (obj.sender) {
          if (typeof obj.sender.name === 'string' && obj.sender.name.match(/^User_[a-zA-Z0-9]+/)) {
            obj.sender.name = 'User';
          }
          if (typeof obj.sender.username === 'string' && obj.sender.username.match(/^User_[a-zA-Z0-9]+/)) {
            obj.sender.username = 'User';
          }
        }
        
        return obj;
      };
      
      // Process and sanitize each request
      const processedRequests = requestsArray.map(request => {
        // First sanitize any user ID patterns in names
        const sanitizedRequest = sanitizeNames(request);
        
        // Then process the request data
        return {
          ...sanitizedRequest,
          // Ensure we have a display name that's not a user ID
          displayName: getSenderDisplayName(sanitizedRequest),
          // Format dates if they exist
          created_at: sanitizedRequest.created_at ? new Date(sanitizedRequest.created_at).toISOString() : null,
          updated_at: sanitizedRequest.updated_at ? new Date(sanitizedRequest.updated_at).toISOString() : null
        };
      });
      
      setRequests(processedRequests);
    } catch (error) {
      setError('Failed to load share requests. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle accept button click
  const handleAccept = async (request) => {
    if (!user?.id) return;
    
    try {
      setFeedback({ type: 'loading', message: 'Accepting invitation...' });
      
      // Call the respondToCalendarShareRequest action (which will also refresh data)
      await dispatch(respondToCalendarShareRequest({
        requestId: request.id,
        userId: user.id,
        action: 'accept'
      }));
      
      // Remove the request from the local state
      setRequests(prev => prev.filter(req => req.id !== request.id));
      
      // Trigger the onAccept callback if provided
      if (onAccept && typeof onAccept === 'function') {
        onAccept(request);
      }
      
      // Show success feedback
      setFeedback({ type: 'success', message: 'Invitation accepted successfully!' });
      
      // Refresh shared events after a successful acceptance
      dispatch(fetchSharedEvents(user.id));
    } catch (error) {
      setFeedback({ type: 'error', message: 'Failed to accept invitation. Please try again.' });
      setError('Failed to accept invitation. Please try again.');
    }
  };
  
  // Handle reject button click
  const handleReject = async (request) => {
    if (!user?.id) return;
    
    try {
      setFeedback({ type: 'loading', message: 'Rejecting invitation...' });
      
      // Call the respondToCalendarShareRequest action
      await dispatch(respondToCalendarShareRequest({
        requestId: request.id,
        userId: user.id,
        action: 'reject'
      }));
      
      // Remove the request from the local state
      setRequests(prev => prev.filter(req => req.id !== request.id));
      
      // Trigger the onReject callback if provided
      if (onReject && typeof onReject === 'function') {
        onReject(request);
      }
      
      // Show success feedback
      setFeedback({ type: 'success', message: 'Invitation rejected.' });
    } catch (error) {
      setFeedback({ type: 'error', message: 'Failed to reject invitation. Please try again.' });
      setError('Failed to reject invitation. Please try again.');
    }
  };
  
  // Format event time
  const formatEventTime = (event) => {
    if (!event) return 'Time not specified';
    
    try {
      // Check for the existence of time-related fields
      if (!event.startTime && !event.start) {
        return 'Time not specified';
      }
      
      // Try to parse the start and end times
      const start = parseISO(event.startTime || event.start);
      
      // Format the start time
      const formattedStart = format(start, 'h:mm a');
      
      // Try to get the end time if available
      if (event.endTime || event.end) {
        const end = parseISO(event.endTime || event.end);
        const formattedEnd = format(end, 'h:mm a');
        return `${formattedStart} - ${formattedEnd}`;
      }
      
      return formattedStart;
    } catch (err) {
      return 'Time not specified';
    }
  };
  
  // Format event date
  const formatEventDate = (event) => {
    if (!event) return 'Date not specified';
    
    try {
      // Check for the existence of date-related fields
      if (!event.startTime && !event.start) {
        return 'Date not specified';
      }
      
      const start = parseISO(event.startTime || event.start);
      if (isNaN(start.getTime())) {
        return 'Date not specified';
      }
      
      return format(start, 'EEEE, MMMM d, yyyy');
    } catch (err) {
      return 'Date not specified';
    }
  };
  
  // Add this section to render feedback messages in the UI
  const renderFeedback = () => {
    if (!feedback) return null;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className={`fixed top-4 right-4 z-50 p-3 rounded-lg text-white ${
          feedback.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        }`}
      >
        {feedback.message}
      </motion.div>
    );
  };
  
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          onClick={onClose}
        >
          <motion.div
            className="relative w-full max-w-md mx-4 overflow-hidden rounded-2xl bg-pri-dark shadow-2xl border border-soft-pink/20"
            variants={dialogVariants}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Glossy Effect Overlays */}
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-gradient-to-br from-soft-pink/10 via-transparent to-soft-pink/10" />
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-pri-white/5 to-transparent" />
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-soft-pink/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-soft-pink/10 to-transparent" />
            </div>
            
            <div className="relative">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-soft-pink/20 bg-gradient-to-r from-sec-dark/90 to-pri-dark/90">
                <h2 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-soft-pink to-pri-white flex items-center">
                  <FaCalendarAlt className="inline-block mr-2 text-soft-pink" /> Calendar Invitations
                </h2>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-full hover:bg-soft-pink/10 text-pri-gray hover:text-pri-white transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              
              {/* Content */}
              {isLoading ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-4 text-center"
                >
                  <div className="flex justify-center">
                    <svg className="w-10 h-10 animate-spin" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  </div>
                  <p className="mt-2 text-pri-gray">Loading your calendar invitations...</p>
                </motion.div>
              ) : error ? (
                <motion.div 
                  className="py-6 text-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <div className="bg-red-900/30 border border-red-500/30 rounded-lg p-3 mb-3">
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                  <motion.button 
                    onClick={loadShareRequests}
                    className="px-4 py-1.5 bg-gradient-to-r from-blue-600/80 to-blue-800/80 hover:from-blue-500/80 hover:to-blue-700/80 rounded-md text-white text-sm shadow-lg border border-blue-500/40 transition-all"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Try Again
                  </motion.button>
                </motion.div>
              ) : requests.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-center py-6"
                >
                  <div className="flex justify-center">
                    <FaCalendarCheck className="h-10 w-10 text-green-400/60 mb-2" />
                  </div>
                  <h3 className="text-white text-base font-medium mb-1">No Pending Invitations</h3>
                  <p className="text-gray-400 text-xs">
                    You currently don't have any pending calendar invitations
                  </p>
                </motion.div>
              ) : (
                <div className="space-y-3 py-1">
                  {requests.map((request, index) => (
                    <motion.div
                      key={request.id}
                      custom={index}
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-lg overflow-hidden shadow-lg border border-gray-700/50 backdrop-blur-sm"
                    >
                      {/* Invitation tile with 2x2 grid layout */}
                      <div className="bg-gradient-to-r from-soft-pink/10 to-soft-pink/20 p-3 grid grid-cols-[1fr_auto] gap-x-3">
                        {/* Left column - top (event name and sender) */}
                        <div className="flex items-center">
                          {request.profile_photo_url || request.sender?.profile_photo_url ? (
                            <div className="w-8 h-8 rounded-full border border-soft-pink/40 shadow-lg overflow-hidden mr-2 flex-shrink-0">
                              <img 
                                src={request.profile_photo_url || request.sender?.profile_photo_url} 
                                alt={`${getSenderDisplayName(request)}'s profile`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentNode.innerHTML = `<div class="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-soft-pink/20 to-soft-pink/30 border border-soft-pink/40 text-soft-pink shadow-lg"><svg class="h-3.5 w-3.5" stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 448 512" xmlns="http://www.w3.org/2000/svg"><path d="M224 256c70.7 0 128-57.3 128-128S294.7 0 224 0 96 57.3 96 128s57.3 128 128 128zm89.6 32h-16.7c-22.2 10.2-46.9 16-72.9 16s-50.6-5.8-72.9-16h-16.7C60.2 288 0 348.2 0 422.4V464c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48v-41.6c0-74.2-60.2-134.4-134.4-134.4z"></path></svg></div>`;
                                }}
                              />
                            </div>
                          ) : (
                            <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-gradient-to-br from-soft-pink/20 to-soft-pink/30 border border-soft-pink/40 text-soft-pink shadow-lg mr-2">
                              <FaUser className="h-3.5 w-3.5" />
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-x-1.5">
                              <h3 className="text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-soft-pink to-pri-white line-clamp-1">
                                {request.event?.title || 'Untitled Event'}
                              </h3>
                              
                              {/* Description icon - if available */}
                              {request.event?.description && (
                                <div className="relative group">
                                  <div className="flex-shrink-0 w-4 h-4 flex items-center justify-center rounded-full bg-soft-pink/10 text-soft-pink border border-soft-pink/20 cursor-help">
                                    <FaInfoCircle className="h-2 w-2" />
                                  </div>
                                  <div className="absolute left-0 top-full mt-1 w-48 bg-gray-800 border border-soft-pink/20 p-2 rounded-md shadow-lg z-10 text-xs text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {request.event.description}
                                  </div>
                                </div>
                              )}
                            </div>
                            <p className="text-xs text-gray-300 flex items-center space-x-1">
                              <span className="font-medium text-soft-pink">
                                {getSenderDisplayName(request)}
                              </span>
                            </p>
                          </div>
                        </div>
                        
                        {/* Right column - accept/decline buttons (spans 2 rows) */}
                        <div className="flex flex-col justify-center space-y-2 row-span-2">
                          {request.processing ? (
                            <div className="inline-flex items-center justify-center px-2 py-1 rounded-full bg-gray-800/50 text-gray-400 text-xs">
                              <div className="animate-spin h-3 w-3 mr-1 border-t-2 border-b-2 border-soft-pink rounded-full"></div>
                              <span>...</span>
                            </div>
                          ) : (
                            <>
                              <button
                                onClick={() => handleAccept(request)}
                                className="w-9 h-9 flex items-center justify-center rounded-full transition-all bg-green-900/40 hover:bg-green-800/60 text-green-300 hover:text-green-200 border border-green-700/30 hover:border-green-600/50 shadow-sm hover:shadow-md"
                                title="Accept"
                              >
                                <FaCheck className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => handleReject(request)}
                                className="w-9 h-9 flex items-center justify-center rounded-full transition-all bg-red-900/40 hover:bg-red-800/60 text-red-300 hover:text-red-200 border border-red-700/30 hover:border-red-600/50 shadow-sm hover:shadow-md"
                                title="Decline"
                              >
                                <FaTimes className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                        
                        {/* Left column - bottom (date, time and location) */}
                        <div className="flex items-center mt-2 gap-x-2 flex-wrap">
                          {/* Date */}
                          <div className="flex items-center">
                            <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-soft-pink/10 text-soft-pink border border-soft-pink/20 mr-1">
                              <FaCalendarAlt className="h-2.5 w-2.5" />
                            </div>
                            <div className="text-gray-300 text-xs whitespace-nowrap">
                              {formatEventDate(request.event).split(', ')[1]}
                            </div>
                          </div>
                          
                          {/* Time */}
                          <div className="flex items-center">
                            <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-soft-pink/10 text-soft-pink border border-soft-pink/20 mr-1">
                              <FaClock className="h-2.5 w-2.5" />
                            </div>
                            <div className="text-gray-300 text-xs">
                              {formatEventTime(request.event)}
                            </div>
                          </div>
                          
                          {/* Location - if available */}
                          {request.event?.location && (
                            <div className="flex items-center">
                              <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-soft-pink/10 text-soft-pink border border-soft-pink/20 mr-1">
                                <FaMapMarkerAlt className="h-2.5 w-2.5" />
                              </div>
                              <div className="text-gray-300 text-xs line-clamp-1">
                                {request.event.location}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
      {renderFeedback()}
    </AnimatePresence>
  );
};

export default CalendarShareRequestsDialog; 